/**
 * ChatConnectionBanner
 * Small non-blocking top banner shown when the chat websocket is not connected.
 * Debounced so brief/normal reconnects don't flash on screen.
 */

import { useEffect, useState } from 'react';
import { Loader2, WifiOff } from 'lucide-react';
import { useSocketConnection } from '@/hooks/useSocketConnection';

const SHOW_DELAY_MS = 1500;

export const ChatConnectionBanner = () => {
  const state = useSocketConnection();
  const [visible, setVisible] = useState(false);

  // Hide immediately once connected (adjust state during render on transition)
  const [prevState, setPrevState] = useState(state);
  if (state !== prevState) {
    setPrevState(state);
    if (state === 'connected') {
      setVisible(false);
    }
  }

  useEffect(() => {
    if (state === 'connected') return;
    // Only surface if the disconnected/reconnecting state persists
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [state]);

  if (!visible || state === 'connected') return null;

  const isReconnecting = state === 'reconnecting';

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
      <div
        role="status"
        aria-live="polite"
        className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full shadow-lg text-xs font-medium ${
          isReconnecting
            ? 'bg-amber-500 text-white'
            : 'bg-red-600 text-white'
        }`}
      >
        {isReconnecting ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <WifiOff className="w-3.5 h-3.5" />
        )}
        <span>
          {isReconnecting ? 'Преизграждане на връзката…' : 'Няма връзка'}
        </span>
      </div>
    </div>
  );
};

export default ChatConnectionBanner;
